/**
 * ui/ApercuArticle — rendu GRAPHIQUE d'un article du magasin
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * ─── POURQUOI UN RENDU, ET PAS UNE LISTE ─────────────────────────────────────
 *
 * Un article est une configuration d'écran : couleurs, logo, ordre des langues, pavés de
 * service, proportion réservée à la file. Le décrire par « 6 services, 4 guichets » ne dit
 * rien de ce que le client verra dans sa salle d'attente — or c'est exactement la question
 * qu'on se pose avant de distribuer un article, et la seule réponse fiable était jusqu'ici
 * de l'appliquer sur un site pour aller regarder.
 *
 * Cet aperçu reproduit la mise en page de `/display` en miniature, à partir du MÊME arbre
 * de configuration. Il ne prétend pas être pixel pour pixel : il montre les couleurs, le
 * logo, les langues affichées et dans quel ordre, et l'équilibre entre les pavés de service
 * et la colonne des tickets. C'est ce qui se décide d'un coup d'œil.
 *
 * Aucune dépendance : styles en ligne, dérivés de la configuration. Un aperçu qui exigerait
 * la feuille de style du serveur de site ne serait pas affichable dans la console.
 */

import React from 'react';

/** Langues des noms de service, dans l'ordre RÉGLÉ — même règle que l'écran et le serveur. */
function languesAffichees(d = {}) {
  return [
    { cle: 'ar', ordre: Number(d.serviceNameOrderAr ?? 1), vu: d.showServiceNameAr !== false, champ: 'nameAr', rtl: true },
    { cle: 'en', ordre: Number(d.serviceNameOrderEn ?? 2), vu: d.showServiceNameEn !== false, champ: 'nameEn', rtl: false },
    { cle: 'fr', ordre: Number(d.serviceNameOrderFr ?? 3), vu: d.showServiceNameFr !== false, champ: 'name', rtl: false },
  ].filter((l) => l.vu).sort((a, b) => a.ordre - b.ordre);
}

/** Code du ticket tel que le serveur le composera — espace compris. */
function exempleLibelle(service, langues, data) {
  const choisi = data?.display?.ticketCodeLang;
  const langue = ['ar', 'fr', 'en'].includes(choisi) ? choisi : (langues[0]?.cle || 'fr');
  const champ = { ar: 'codeAr', en: 'codeEn', fr: 'codeFr' }[langue] || 'codeFr';
  const code = service?.[champ];
  return `${code && String(code).trim() ? code : service?.id || '—'} 12`;
}

/** Un média de l'article, en `data:` — `null` si l'article n'a pas été chargé en entier. */
function sourceLogo(medias) {
  const logo = (medias || []).find((m) => /logo/i.test(m.nom) && m.contenu);
  if (!logo) return null;
  const ext = String(logo.nom).split('.').pop().toLowerCase();
  const type = ext === 'svg' ? 'image/svg+xml' : `image/${ext === 'jpg' ? 'jpeg' : ext}`;
  return `data:${type};base64,${logo.contenu}`;
}

export default function ApercuArticle({ data, medias, hauteur = 150 }) {
  const styles = data?.styles?.display || {};
  const langues = languesAffichees(styles);
  const services = (data?.serviceTypes || []).slice(0, 6);
  const couleur = data?.ui?.primaryColor || '#607D8B';
  const logo = sourceLogo(medias);

  // Proportion réglable de la colonne des tickets (`ticketsPanelPercent`), bornée comme
  // sur l'écran : sous 15 % la colonne devient illisible, au-delà de 60 % elle mange les
  // services. Montrer la valeur réelle est tout l'intérêt de l'aperçu.
  const partTickets = Math.min(60, Math.max(15, Number(styles.ticketsPanelPercent ?? 33)));
  const colonne = String(styles.panelsLayout || 'row') === 'column';

  const cadre = {
    height: hauteur, display: 'flex', flexDirection: 'column',
    background: '#0f172a', borderRadius: 6, overflow: 'hidden',
    fontSize: Math.max(5, hauteur / 22), lineHeight: 1.25, color: '#fff',
  };

  return (
    <div style={cadre} aria-label="Aperçu de l'écran d'affichage">
      {/* Bandeau : logo et identité, sur la couleur principale de l'article */}
      <div style={{ background: couleur, padding: `${hauteur / 30}px ${hauteur / 20}px`, display: 'flex', alignItems: 'center', gap: hauteur / 25, flex: '0 0 auto' }}>
        {logo
          ? <img src={logo} alt="" style={{ height: hauteur / 8, width: 'auto', objectFit: 'contain' }} />
          : <div style={{ height: hauteur / 8, width: hauteur / 8, borderRadius: 3, background: 'rgba(255,255,255,.28)' }} />}
        <div style={{ overflow: 'hidden' }}>
          <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {data?.agency?.name || 'Agence'}
          </div>
          {data?.agency?.nameAr && (
            <div dir="rtl" style={{ opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {data.agency.nameAr}
            </div>
          )}
        </div>
      </div>

      {/* Corps : pavés de service et colonne des tickets, dans leur proportion réelle */}
      <div style={{ display: 'flex', flexDirection: colonne ? 'column' : 'row', flex: 1, minHeight: 0, gap: 2, padding: 2 }}>
        <div style={{
          flex: `1 1 ${100 - partTickets}%`, minWidth: 0, minHeight: 0,
          display: 'grid', gap: 2,
          gridTemplateColumns: `repeat(${Math.min(3, Math.max(1, Math.ceil(Math.sqrt(services.length || 1))))}, 1fr)`,
        }}>
          {services.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>aucun service</div>
          )}
          {services.map((s) => (
            <div key={s.id} style={{
              background: s.color || couleur, borderRadius: 3, minWidth: 0,
              padding: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', overflow: 'hidden',
            }}>
              {/* Les langues s'empilent dans l'ordre réglé — c'est ce qu'on vient vérifier */}
              {langues.map((l) => (
                <div key={l.cle} dir={l.rtl ? 'rtl' : undefined} style={{
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
                  fontWeight: l === langues[0] ? 700 : 400, opacity: l === langues[0] ? 1 : 0.8,
                }}>
                  {s[l.champ] || s.name || s.id}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={{ flex: `0 0 ${partTickets}%`, minWidth: 0, minHeight: 0, background: '#1e293b', borderRadius: 3, padding: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {services.slice(0, 3).map((s) => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 3, background: 'rgba(255,255,255,.08)', borderRadius: 2, padding: '1px 3px' }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{exempleLibelle(s, langues, data)}</span>
              <span style={{ opacity: 0.7 }}>{data?.counters?.[0]?.name ? String(data.counters[0].name).slice(0, 6) : 'G1'}</span>
            </div>
          ))}
          {services.length === 0 && <div style={{ opacity: 0.5, textAlign: 'center', marginTop: 'auto', marginBottom: 'auto' }}>—</div>}
        </div>
      </div>
    </div>
  );
}
